import React from 'react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import {
  Target,
  Eye,
  Award,
  Users,
  Shield,
  TrendingUp,
  CheckCircle2,
  Building2,
} from 'lucide-react';

const AboutPage = () => {
  const values = [
    {
      icon: Shield,
      title: 'Trust & Transparency',
      description:
        'We believe in complete transparency with no hidden charges and honest advice.',
    },
    {
      icon: Award,
      title: 'Excellence',
      description:
        'Committed to delivering the highest quality services with professional expertise.',
    },
    {
      icon: Users,
      title: 'Client-Centric',
      description:
        'Your success is our priority. Dedicated support at every step.',
    },
    {
      icon: TrendingUp,
      title: 'Innovation',
      description:
        'Leveraging technology to simplify complex legal and compliance processes.',
    },
  ];

  const achievements = [
    {
      icon: Users,
      number: '500+',
      label: 'Happy Clients',
    },
    {
      icon: Building2,
      number: '1000+',
      label: 'Services Delivered',
    },
    {
      icon: Award,
      number: '50+',
      label: 'Types of Licenses',
    },
    {
      icon: CheckCircle2,
      number: '100%',
      label: 'Client Satisfaction',
    },
  ];

  const certifications = [
    'CIN: U69200MP2024PTC071073',
    'MSME: UDYAM-MP-23-0142572',
    'GST: 24AADCU5701A1ZZ',
    'DPIIT: DIPP172158',
    'PAN: AADCU5701A',
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-gradient-to-br from-[#0A2463] via-[#0d2d6e] to-[#247BA0] relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-10 left-10 w-72 h-72 bg-[#FFB703] rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-float"></div>
          <div
            className="absolute bottom-10 right-10 w-96 h-96 bg-[#247BA0] rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-float"
            style={{ animationDelay: '1s' }}
          ></div>
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <Badge className="mb-6 bg-[#FFB703] text-white px-6 py-2">
              About Us
            </Badge>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
              Empowering Businesses,
              <br />
              <span className="text-[#FFB703]">Building Trust</span>
            </h1>
            <p className="text-xl text-gray-200 leading-relaxed">
              Your Trusted Partner for Legal, Tax, and Compliance Solutions
            </p>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <Badge className="mb-6 bg-[#0A2463] text-white px-4 py-2">
                  Who We Are
                </Badge>
                <h2 className="text-4xl font-bold text-[#0A2463] mb-6">
                  About Utkarshudyam Solution Private Limited
                </h2>
                <div className="space-y-4 text-gray-600 leading-relaxed">
                  <p>
                    <strong className="text-[#0A2463]">
                      Utkarshudyam Solution Private Limited
                    </strong>{' '}
                    is a professionally managed consultancy firm based in
                    Ahmedabad, Gujarat, dedicated to supporting entrepreneurs,
                    startups, and established businesses with reliable compliance
                    and growth solutions.
                  </p>
                  <p>
                    We specialize in business registrations, government
                    certifications, tax filings, funding assistance, and
                    regulatory approvals. Our mission is to simplify complex legal
                    processes and help businesses grow with confidence,
                    compliance, and credibility.
                  </p>
                  <p>
                    With a team of experienced professionals, we have successfully
                    served 500+ clients across India, delivering 1000+ services
                    with a 100% satisfaction rate. Our commitment to transparency,
                    quality, and customer service has made us one of the most
                    trusted names in the compliance industry.
                  </p>
                </div>
              </div>

              <div className="bg-gradient-to-br from-[#0A2463] to-[#247BA0] rounded-2xl p-8 shadow-2xl">
                <div className="space-y-6">
                  {certifications.map((cert, index) => (
                    <div
                      key={index}
                      className="flex items-center gap-3 bg-white/10 backdrop-blur-sm rounded-lg p-4"
                    >
                      <CheckCircle2 className="text-[#FFB703] flex-shrink-0" size={24} />
                      <span className="text-white font-medium">{cert}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Mission */}
            <Card className="border-none shadow-xl hover:shadow-2xl transition-all">
              <CardContent className="p-8">
                <div className="w-16 h-16 bg-gradient-to-br from-[#0A2463] to-[#247BA0] rounded-xl flex items-center justify-center mb-6 shadow-lg">
                  <Target className="text-white" size={32} />
                </div>
                <h3 className="text-3xl font-bold text-[#0A2463] mb-4">
                  Our Mission
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  To simplify complex legal and compliance processes for Indian
                  businesses, making them accessible, affordable, and efficient.
                  We aim to be the trusted partner that empowers entrepreneurs
                  to focus on growth while we handle their compliance needs.
                </p>
              </CardContent>
            </Card>

            {/* Vision */}
            <Card className="border-none shadow-xl hover:shadow-2xl transition-all">
              <CardContent className="p-8">
                <div className="w-16 h-16 bg-gradient-to-br from-[#247BA0] to-[#FFB703] rounded-xl flex items-center justify-center mb-6 shadow-lg">
                  <Eye className="text-white" size={32} />
                </div>
                <h3 className="text-3xl font-bold text-[#0A2463] mb-4">
                  Our Vision
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  To become India's most trusted and preferred compliance
                  partner, known for excellence, transparency, and innovation.
                  We envision a future where every business, regardless of size,
                  has easy access to professional compliance services.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-[#0A2463] text-white px-4 py-2">
              Our Values
            </Badge>
            <h2 className="text-4xl md:text-5xl font-bold text-[#0A2463] mb-4">
              What Drives Us
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              The principles that guide everything we do
            </p>
          </div>

          <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => {
              const Icon = value.icon;
              return (
                <Card
                  key={index}
                  className="border-none shadow-lg hover:shadow-2xl transition-all hover:-translate-y-2 text-center"
                >
                  <CardContent className="p-6">
                    <div className="w-16 h-16 bg-gradient-to-br from-[#0A2463] to-[#247BA0] rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                      <Icon className="text-white" size={28} />
                    </div>
                    <h3 className="text-xl font-bold text-[#0A2463] mb-3">
                      {value.title}
                    </h3>
                    <p className="text-sm text-gray-600 leading-relaxed">
                      {value.description}
                    </p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Achievements */}
      <section className="py-20 bg-gradient-to-br from-[#0A2463] via-[#0d2d6e] to-[#247BA0] relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-10 right-10 w-96 h-96 bg-[#FFB703] rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-float"></div>
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-[#FFB703] text-white px-4 py-2">
              Achievements
            </Badge>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Our Success in Numbers
            </h2>
          </div>

          <div className="max-w-5xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-8">
            {achievements.map((achievement, index) => {
              const Icon = achievement.icon;
              return (
                <div key={index} className="text-center">
                  <div className="w-20 h-20 bg-white/10 backdrop-blur-sm rounded-full flex items-center justify-center mx-auto mb-4">
                    <Icon className="text-[#FFB703]" size={32} />
                  </div>
                  <div className="text-5xl font-bold text-white mb-2">
                    {achievement.number}
                  </div>
                  <div className="text-gray-200 font-medium">
                    {achievement.label}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Founder Section */}
      <section className="py-20 bg-gradient-to-b from-white to-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <Card className="border-none shadow-2xl overflow-hidden">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8 p-8">
                <div className="md:col-span-1 flex items-center justify-center">
                  <div className="w-48 h-48 bg-gradient-to-br from-[#0A2463] to-[#247BA0] rounded-2xl flex items-center justify-center shadow-xl">
                    <span className="text-white text-6xl font-bold">NP</span>
                  </div>
                </div>
                <div className="md:col-span-2 flex flex-col justify-center">
                  <h3 className="text-3xl font-bold text-[#0A2463] mb-2">
                    Neelesh Patel
                  </h3>
                  <p className="text-xl text-[#247BA0] font-semibold mb-4">
                    Founder & CEO
                  </p>
                  <p className="text-gray-600 leading-relaxed mb-6">
                    With a vision to simplify business compliance in India,
                    Neelesh Patel founded Utkarshudyam Solution to provide
                    transparent, reliable, and affordable compliance services.
                    His commitment to excellence and client satisfaction has been
                    the driving force behind the company's success.
                  </p>
                  <div className="flex flex-wrap gap-3">
                    <Badge className="bg-[#0A2463] text-white px-3 py-1">
                      Entrepreneur
                    </Badge>
                    <Badge className="bg-[#247BA0] text-white px-3 py-1">
                      Compliance Expert
                    </Badge>
                    <Badge className="bg-[#FFB703] text-white px-3 py-1">
                      Business Consultant
                    </Badge>
                  </div>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AboutPage;